import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  setDoc,
  db
} from '../config/firebase';
import { transformDocument } from './firestoreService';
import { COLLECTIONS } from './firestorePaths';

/**
 * OscarsPartyService
 * Handles operations for oscars_parties collection
 */
class OscarsPartyService {
  /**
   * Fetch all Oscar parties, ordered by year descending
   * @returns {Promise<Array<OscarsPartyModel>>} Array of Oscar party models
   */
  async fetchOscarsParties() {
    try {
      const partiesRef = collection(db, COLLECTIONS.OSCARS_PARTIES);
      const q = query(partiesRef, orderBy('year', 'desc'));
      const querySnapshot = await getDocs(q);

      const parties = [];
      querySnapshot.forEach((docSnapshot) => {
        const partyData = transformDocument(docSnapshot.id, docSnapshot.data());
        parties.push(partyData);
      });

      return parties;
    } catch (error) {
      console.error('Error fetching Oscar parties:', error);
      throw error;
    }
  }

  /**
   * Fetch a single Oscar party by document ID
   * @param {string} partyId - The Oscar party document ID
   * @returns {Promise<OscarsPartyModel | null>} Oscar party model or null if not found
   */
  async fetchOscarsPartyById(partyId) {
    try {
      const partyRef = doc(db, COLLECTIONS.OSCARS_PARTIES, partyId);
      const docSnapshot = await getDoc(partyRef);

      if (!docSnapshot.exists()) {
        return null;
      }

      return transformDocument(docSnapshot.id, docSnapshot.data());
    } catch (error) {
      console.error('Error fetching Oscar party by ID:', error);
      throw error;
    }
  }

  /**
   * Update an Oscar party with partial data (merge)
   * @param {string} partyId - The party document ID
   * @param {Object} updates - Partial update object with camelCase keys
   * @param {number} [updates.totalBallots] - Total ballots count
   * @param {number} [updates.ballotEntryAmount] - Ballot entry amount
   * @returns {Promise<void>}
   */
  async updateOscarsParty(partyId, updates) {
    try {
      const partyRef = doc(db, COLLECTIONS.OSCARS_PARTIES, partyId);

      // Convert camelCase to snake_case for Firestore
      const firestoreUpdates = {};
      if (updates.totalBallots !== undefined) {
        firestoreUpdates.total_ballots = updates.totalBallots;
      }
      if (updates.ballotEntryAmount !== undefined) {
        firestoreUpdates.ballot_entry_amount = updates.ballotEntryAmount;
      }

      // Add date_updated (Unix date in milliseconds)
      firestoreUpdates.date_updated = Date.now();

      await setDoc(partyRef, firestoreUpdates, { merge: true });
    } catch (error) {
      console.error('Error updating Oscar party:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new OscarsPartyService();
